/**
 * Scheduled-publish-time helpers shared by the Library grid and the Job
 * Detail screen. Both surfaces need to answer the same question — "when
 * is this finished clip going out?" — from the provider-agnostic
 * `ScheduledPost[]` returned by `listSchedulingPosts()`.
 *
 * The data source is `dueAt` (ISO-8601, UTC) reused client-side: the
 * Library already fetches the scheduling list for its status pill and
 * just discards the time. No server change — baking the schedule into
 * `/api/library` would couple the server to Buffer on every page load,
 * and scheduling is meant to move off Buffer (see project notes).
 *
 * Pure module — only a `type` import from `../api` (erased at compile),
 * so it carries no React/DOM dependency and is unit-testable in isolation.
 */
import type { ScheduledPost } from "../api";

/**
 * Recover the source jobId from a scheduling post's `videoUrl`. The
 * publish path uploads as `out/<jobId>-ar.mp4` to R2 public storage
 * (the mp4 is uploaded to private R2 and handed to Buffer as a signed
 * /api/public-asset/... URL — see src/lib/publicAssetToken.ts), so the
 * slug is always the last path segment minus the `-ar.mp4` tail.
 *
 * Lifted out of Library.tsx so Job Detail can share the exact same
 * post→job mapping.
 */
export function extractJobIdFromVideoUrl(url: string | undefined): string | null {
  if (!url) return null;
  const m = url.match(/\/out\/([^/]+)-ar\.mp4(?:\?|$)/);
  return m ? m[1]! : null;
}

export interface ScheduledSummary {
  /** Earliest upcoming scheduled time across every channel this job is
   *  queued on. Local `Date` — `dueAt` is stored UTC, rendered local. */
  at: Date;
  /** How many channels this job is scheduled on (≥1). Drives the "+N"
   *  hint when a clip goes out on more than one channel. */
  count: number;
}

/**
 * The earliest *upcoming* scheduled publish for a job, across all channels.
 *
 * Only `status === "scheduled"` posts that map to this job and carry a
 * parseable `dueAt` are considered. When a clip is queued on multiple
 * channels we surface the soonest one (and `count` lets the caller add a
 * "+N" hint). If every match is already in the past — Buffer can lag
 * before flipping a due post to "published" — we still return the latest
 * of them rather than null, so a card reading "Scheduled" never shows a
 * blank time.
 *
 * Returns null only when the job has no scheduled post at all.
 */
export function scheduledSummaryForJob(
  posts: ScheduledPost[],
  jobId: string,
  now: number = Date.now(),
): ScheduledSummary | null {
  const times: number[] = [];
  for (const p of posts) {
    if (p.status !== "scheduled") continue;
    if (extractJobIdFromVideoUrl(p.videoUrl) !== jobId) continue;
    if (!p.dueAt) continue;
    const t = Date.parse(p.dueAt);
    if (Number.isFinite(t)) times.push(t);
  }
  if (times.length === 0) return null;
  times.sort((a, b) => a - b);
  const upcoming = times.find((t) => t >= now);
  // Earliest still-future post if any; otherwise the last (most recent)
  // past one — that's the post Buffer is most likely about to process.
  const chosen = upcoming ?? times[times.length - 1]!;
  return { at: new Date(chosen), count: times.length };
}

/** Zero-padded 24-hour `HH:MM`, matching the render-time stamps the
 *  scheduled line sits beside (Library `formatDate`, JobDetail
 *  `formatRenderedAt`). Deliberately not locale 12-hour. */
function hhmm(d: Date): string {
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  return `${hh}:${mm}`;
}

/** Card line: compact absolute date + 24h time, e.g. `Jun 16, 14:00`.
 *  No relative hint on the card — that lives in the details view only. */
export function formatCardDateTime(at: Date): string {
  const date = at.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${date}, ${hhmm(at)}`;
}

/** Details line: weekday + absolute date + 24h time, e.g.
 *  `Mon, Jun 16 · 14:00`. */
export function formatDetailDateTime(at: Date): string {
  const date = at.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  return `${date} · ${hhmm(at)}`;
}

/**
 * Human relative hint for the details view, e.g. `in 2 days`, `in 5 hours`,
 * `in 12 minutes`. Floors to the largest whole unit (mirrors the Calendar
 * countdown). Anything inside the next minute — or already past — reads
 * `soon`, since a clip whose `dueAt` just lapsed is about to fire.
 */
export function formatRelative(at: Date, now: number = Date.now()): string {
  const ms = at.getTime() - now;
  if (ms < 60_000) return "soon";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `in ${mins} minute${mins === 1 ? "" : "s"}`;
  const hrs = Math.floor(ms / 3_600_000);
  if (hrs < 24) return `in ${hrs} hour${hrs === 1 ? "" : "s"}`;
  const days = Math.floor(ms / 86_400_000);
  return `in ${days} day${days === 1 ? "" : "s"}`;
}
